import * as React from "react";
import { observer } from "mobx-react";
import { useDialog } from "@shared-ui/dialog-context";
import MonthChart from "./MonthChart/MonthChart";
import { TripCostEstimatorProps, TripCostEstimatorHotelCost, TripCostEstimatorFTNodeOptions } from "./typings";
import { LinkListView } from "./LinkListView/LinkListView";
import TripCostDialog from "./TripCostDialog/TripCostDialog";
import { UitkCard, UitkCardContentSection, UitkCardActionArea } from "uitk-react-cards";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading, UitkText } from "uitk-react-text";
import {
  sendImmediateClickstreamTrackEvent,
  Action,
  FlexTrackingInfo,
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { Viewport, ViewSmall, ViewLarge } from "@shared-ui/viewport-context";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { withStores } from "stores";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { useLocalization } from "@shared-ui/localization-context";

enum EVENT_EMPTY_DATA {
  MONTH = "month.empty",
  RESPONSE = "response.empty",
}

export const TripCostEstimator = withStores(
  "analytics",
  "flexModuleModelStore",
  "context"
)(
  observer((props: TripCostEstimatorProps) => {
    const moduleContainer = React.createRef<HTMLDivElement>();
    let popover: { closePopover: () => void };
    const track = useClickTracker();
    const [isDialogOpen, dialogActions] = useDialog("tripCostDialog");

    const { flexTemplateNode, flexModuleModelStore, context } = props;
    const { id } = flexTemplateNode;
    const l10n = useLocalization();
    const { formatText } = l10n;

    const model = flexModuleModelStore.getModel(id) as TripCostEstimatorFTNodeOptions | null;
    if (!model) {
      return null;
    }
    const { content } = model;

    const destinationTrendPrice = content && content.destinationTrendPrice;
    const hasEmptyResponse = destinationTrendPrice && destinationTrendPrice.hasEmptyResponse;
    const hasEmptyMonthAvgPrice = destinationTrendPrice && destinationTrendPrice.hasEmptyMonthAvgPrice;

    const showPopover = Boolean(model && model.showPopover);
    const hasVisibleEmptyMonthAvgPrice = destinationTrendPrice && destinationTrendPrice.hasVisibleEmptyMonthAvgPrice;
    const hotelCostByStars = (destinationTrendPrice && destinationTrendPrice.hotelCostByStars) || [];

    const cheapestMonthCost =
      destinationTrendPrice && destinationTrendPrice.cheapestMonth && destinationTrendPrice.cheapestMonth.cost;
    const expensiveMonthCost =
      destinationTrendPrice && destinationTrendPrice.expensiveMonth && destinationTrendPrice.expensiveMonth.cost;

    const localizedName = context.searchContext.location.localizedName;

    const defaultTitle = formatText("tripCostEstimator.title", localizedName);
    const defaultDialogTitle = formatText("tripCostEstimator.dialog.title", localizedName);

    //helper functions:
    const sendTrackEmptyData = (rfrr: string) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: "tripCostEstimator",
        rfrr,
        action: Action.IMPRESSION,
      };

      sendImmediateClickstreamTrackEvent(flexTrackingInfo, track);
    };

    const closeDialog = () => {
      dialogActions.closeDialog();

      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: "tripCostEstimator",
        rfrr: "overlay.close",
        action: Action.CLICK,
      };

      sendImmediateClickstreamTrackEvent(flexTrackingInfo, track);
    };

    const redirectHSR = (hotelCost?: TripCostEstimatorHotelCost) => {
      if (hotelCost) {
        location.href = hotelCost.url;
      }
    };

    const handleRenderedGraph = (innerPopover: { closePopover: () => void }) => {
      popover = innerPopover;
    };

    const closePopover = () => {
      if (popover) {
        popover.closePopover();
      }
    };

    const Dialog = (
      <TripCostDialog
        title={defaultDialogTitle}
        onClickList={redirectHSR}
        close={closeDialog}
        hotelCostList={hotelCostByStars}
        isOpen={isDialogOpen}
      />
    );

    const keyHelper = new ItemKeyHelper("link");

    const seoLinks = hotelCostByStars.map((hotelCost) => (
      <TrackedLink
        key={keyHelper.next()}
        moduleName={model.flexComponentName}
        rfrr="monthChart"
        linkName={hotelCost.month}
        href={hotelCost.url}
      >
        <span>{`${hotelCost.month} $${hotelCost.avgTwoStarsCost}-${hotelCost.avgFourStarsCost}`}</span>
      </TrackedLink>
    ));

    if (hasEmptyResponse) {
      sendTrackEmptyData(EVENT_EMPTY_DATA.RESPONSE);
    }

    if (hasEmptyMonthAvgPrice) {
      sendTrackEmptyData(EVENT_EMPTY_DATA.MONTH);
    }

    if (hasEmptyResponse || !hotelCostByStars.length) {
      return null;
    }

    return (
      <UitkSpacing padding={{ block: "three", inline: "three" }}>
        <div className="TripCostEstimator" data-fm={flexTemplateNode.options.fmId}>
          <UitkCard>
            <div id={id} ref={moduleContainer} onClick={closePopover}>
              <UitkCardContentSection>
                <Viewport>
                  <ViewSmall>
                    <>
                      <UitkSpacing padding={{ blockend: "four" }}>
                        <UitkHeading tag="h2" size={4}>
                          {defaultTitle}
                        </UitkHeading>
                      </UitkSpacing>
                      <MonthChart
                        showPopover={showPopover}
                        id={`graph-${id}`}
                        data={hotelCostByStars}
                        low={cheapestMonthCost}
                        high={expensiveMonthCost}
                        onClick={redirectHSR}
                        handleRenderedGraph={handleRenderedGraph}
                        isMobile
                      />
                    </>
                  </ViewSmall>
                  <ViewLarge>
                    <>
                      <UitkSpacing padding={{ blockend: "four" }}>
                        <UitkHeading tag="h2" size={3}>
                          {defaultTitle}
                        </UitkHeading>
                      </UitkSpacing>
                      <MonthChart
                        showPopover={showPopover}
                        id={`graph-${id}`}
                        data={hotelCostByStars}
                        low={cheapestMonthCost}
                        high={expensiveMonthCost}
                        onClick={redirectHSR}
                        handleRenderedGraph={handleRenderedGraph}
                        isMobile={false}
                      />
                    </>
                  </ViewLarge>
                </Viewport>
                {hasVisibleEmptyMonthAvgPrice && (
                  <UitkText size={200}>{formatText("tripCostEstimator.emptyPrice")}</UitkText>
                )}

                <UitkText size={200}>{formatText("tripCostEstimator.footer", 2018, "Expedia.com")}</UitkText>
                <UitkText size={200}>{formatText("tripCostEstimator.allPrices", "USD")}</UitkText>
              </UitkCardContentSection>
              <UitkCardActionArea>
                <LinkListView text={formatText("tripCostEstimator.viewList")}>{Dialog}</LinkListView>
                <div className="is-visually-hidden">{seoLinks}</div>
              </UitkCardActionArea>
            </div>
          </UitkCard>
        </div>
      </UitkSpacing>
    );
  })
);

export default TripCostEstimator;
